/**
 * CreditInventoryKit — shared visual primitives for credit / private-asset
 * inventory lists.
 *
 * These mirror the polished "Referential Master" loan table
 * (CreditLoanReferenceWorkspace) exactly so that every credit inventory screen
 * — LL Loans, PC Loans, and the Reference workspace — reads as one product.
 * Purely presentational: no data access, no navigation, no business logic.
 *
 * Two-tier row actions:
 *   NavAction       — calm monochrome icon (navigation / drill-downs)
 *   LifecycleAction — subtle semantic tint (mutating servicing actions)
 * Both share the same 32×32 footprint so the cluster stays aligned, and both
 * carry an instant high-contrast tooltip chip (RowActionTip).
 */
import React from 'react';
import styled from 'styled-components';

export const InventoryLayout = styled.div`
  display: grid;
  gap: 1rem;
`;

export const InventoryToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

export const InventoryFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex: 1;
  flex-wrap: wrap;
`;

export const InventoryActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

export const InventorySearchBox = styled.label`
  min-width: min(420px, 100%);
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 38px;
  padding: 0 0.75rem;
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 8px;
  background: rgba(2, 6, 23, 0.72);
  color: #64748b;

  &:focus-within { border-color: #38bdf8; }

  input {
    width: 100%;
    border: 0;
    outline: 0;
    background: transparent;
    color: #f8fafc;
    font-size: 0.82rem;
  }
`;

export const InventorySelect = styled.select`
  min-height: 38px;
  padding: 0 0.75rem;
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 8px;
  background: rgba(2, 6, 23, 0.72);
  color: #e2e8f0;
  cursor: pointer;
`;

export const InventoryButton = styled.button<{ $primary?: boolean }>`
  min-height: 38px;
  padding: 0 0.8rem;
  border: 1px solid ${({ $primary }) => ($primary ? '#2563eb' : 'rgba(148, 163, 184, 0.24)')};
  border-radius: 8px;
  background: ${({ $primary }) => ($primary ? '#2563eb' : 'rgba(15, 23, 42, 0.72)')};
  color: #f8fafc;
  font-size: 0.76rem;
  font-weight: 750;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  cursor: pointer;

  &:hover { border-color: #38bdf8; }
`;

export const InventoryTableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 8px;
`;

export const InventoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 1040px;

  th {
    padding: 0.7rem 0.8rem;
    text-align: left;
    color: #94a3b8;
    background: rgba(15, 23, 42, 0.86);
    font-size: 0.66rem;
    text-transform: uppercase;
    font-weight: 800;
    white-space: nowrap;
  }

  td {
    padding: 0.75rem 0.8rem;
    border-top: 1px solid rgba(148, 163, 184, 0.12);
    color: #cbd5e1;
    font-size: 0.78rem;
    vertical-align: middle;
  }

  tbody tr:hover {
    background: rgba(148, 163, 184, 0.04);
  }
`;

export const CellPrimary = styled.div<{ $clickable?: boolean }>`
  color: #f8fafc;
  font-weight: 750;
  ${({ $clickable }) => $clickable && `
    cursor: pointer;
    &:hover {
      text-decoration: underline;
      text-decoration-color: rgba(56, 189, 248, 0.5);
      text-underline-offset: 3px;
    }
  `}
`;

export const CellSub = styled.div`
  margin-top: 0.15rem;
  color: #64748b;
  font-size: 0.68rem;
`;

/** Amber "awaiting evidence / awaiting terms" note used inside cells. */
export const InventoryAwaiting = styled.span`
  color: #fbbf24;
  font-size: 0.72rem;
  font-weight: 600;
`;

export const SourceBadge = styled.span<{ $tone?: 'cyan' | 'violet' }>`
  display: inline-flex;
  padding: 0.18rem 0.42rem;
  border-radius: 5px;
  border: 1px solid ${({ $tone }) => ($tone === 'violet' ? 'rgba(167,139,250,.35)' : 'rgba(56,189,248,.35)')};
  color: ${({ $tone }) => ($tone === 'violet' ? '#c4b5fd' : '#7dd3fc')};
  font-size: 0.64rem;
  font-weight: 800;
`;

export const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.28rem;
  flex-wrap: nowrap;
  white-space: nowrap;
`;

/** Thin separator between the monochrome nav tier and the tinted lifecycle tier. */
export const RowActionDivider = styled.span`
  width: 1px;
  height: 20px;
  background: rgba(148, 163, 184, 0.22);
  margin: 0 0.15rem;
  flex: 0 0 auto;
`;

/* 28×28 (slightly tighter than the Reference table's 32px) so the full
   two-tier action set fits on ONE line without wrapping. */
export const NavActionButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 7px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: rgba(15, 23, 42, 0.76);
  color: #bae6fd;
  display: grid;
  place-items: center;
  cursor: pointer;
  flex: 0 0 auto;

  svg { width: 14px; height: 14px; }
  &:hover:not(:disabled) { border-color: #38bdf8; color: #f8fafc; }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`;

export const LifecycleActionButton = styled.button<{ $color: string }>`
  width: 28px;
  height: 28px;
  border-radius: 7px;
  border: 1px solid ${({ $color }) => `${$color}55`};
  background: ${({ $color }) => `${$color}18`};
  color: ${({ $color }) => $color};
  display: grid;
  place-items: center;
  cursor: pointer;
  flex: 0 0 auto;
  transition: border-color 0.16s ease, background 0.16s ease;

  svg { width: 14px; height: 14px; }
  &:hover:not(:disabled) {
    border-color: ${({ $color }) => `${$color}aa`};
    background: ${({ $color }) => `${$color}28`};
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

export const InventoryState = styled.div<{ $error?: boolean }>`
  padding: 1rem;
  border: 1px solid ${({ $error }) => ($error ? 'rgba(248,113,113,.3)' : 'rgba(148,163,184,.16)')};
  border-radius: 8px;
  color: ${({ $error }) => ($error ? '#fecaca' : '#94a3b8')};
  font-size: 0.8rem;
`;

export const InventoryFootnote = styled.div`
  margin-top: 0.15rem;
  color: #64748b;
  font-size: 0.68rem;
`;

/**
 * Instant, high-contrast tooltip chip. Right-anchored above the button so it
 * never clips at the table's right edge (the table lives in an overflow-x
 * scroll container). Shows immediately on hover — unlike the delayed, faint
 * native title=.
 */
export const RowActionTip = styled.span`
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;

  &::after {
    content: attr(data-tip);
    position: absolute;
    bottom: calc(100% + 7px);
    right: 0;
    white-space: nowrap;
    background: #0b1220;
    color: #f1f5f9;
    border: 1px solid rgba(148, 163, 184, 0.42);
    border-radius: 6px;
    padding: 0.32rem 0.5rem;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.01em;
    line-height: 1;
    box-shadow: 0 10px 28px rgba(2, 6, 23, 0.72);
    opacity: 0;
    pointer-events: none;
    z-index: 60;
    transition: opacity 0.12s ease;
  }

  &:hover::after,
  &:focus-within::after {
    opacity: 1;
  }
`;

/** Monochrome navigation icon + tooltip. */
export const NavAction: React.FC<{
  tip: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ tip, onClick, disabled = false, children }) => (
  <RowActionTip data-tip={tip}>
    <NavActionButton type="button" aria-label={tip} disabled={disabled} onClick={onClick}>
      {children}
    </NavActionButton>
  </RowActionTip>
);

/** Semantically tinted lifecycle-mutation icon + tooltip. */
export const LifecycleAction: React.FC<{
  tip: string;
  color: string;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ tip, color, onClick, children }) => (
  <RowActionTip data-tip={tip}>
    <LifecycleActionButton type="button" aria-label={tip} $color={color} onClick={onClick}>
      {children}
    </LifecycleActionButton>
  </RowActionTip>
);
